import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import type { InvitedDirectReport } from '../types'
import { DataTable } from '@/components/common/data-table'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface InvitedDirectReportsTableProps {
  invites: Array<InvitedDirectReport>
  isLoading?: boolean
  currentPage?: number
  totalPages?: number
  totalInvites?: number
  onPageChange?: (page: number) => void
}

export function InvitedDirectReportsTable({
  invites,
  isLoading = false,
  currentPage = 1,
  totalPages = 1,
  totalInvites = 0,
  onPageChange,
}: InvitedDirectReportsTableProps) {
  // Define columns using TanStack Table
  const columns = useMemo<Array<ColumnDef<InvitedDirectReport>>>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <div className="font-medium">{row.original.email}</div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status.toLowerCase()
          const variant =
            status === 'pending'
              ? 'secondary'
              : status === 'accepted'
                ? 'default'
                : status === 'expired'
                  ? 'destructive'
                  : 'outline'

          return (
            <Badge variant={variant} className="capitalize">
              {status || 'unknown'}
            </Badge>
          )
        },
      },
      {
        id: 'roles',
        header: 'Roles',
        cell: ({ row }) => {
          const roles = row.original.roles

          return (
            <div className="flex flex-wrap gap-1">
              {roles.length > 0 ? (
                roles.map((role, index) => (
                  <Badge key={index} variant="secondary">
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground">No roles</span>
              )}
            </div>
          )
        },
      },
    ],
    [],
  )

  if (isLoading) {
    return (
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="relative w-full overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50 border-b transition-colors">
                <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                  Email
                </TableHead>
                <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                  Status
                </TableHead>
                <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                  Roles
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow
                  key={i}
                  className="hover:bg-muted/50 border-b transition-colors"
                >
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="h-6 w-20 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="flex gap-1">
                      <div className="h-6 w-16 bg-muted animate-pulse rounded" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={invites}
      emptyMessage="No invited direct reports found."
      pagination={onPageChange ? { currentPage, totalPages, totalItems: totalInvites, onPageChange } : undefined}
    />
  )
}
